import React from 'react';
import { 
  User, 
  Store, 
  Truck, 
  Shield, 
  Bot, 
  Clock, 
  AlertCircle,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { cn } from '../../lib/utils';

interface StateHistoryEntry {
  id: string;
  fromState: string;
  toState: string;
  action: string;
  actor: {
    id: string;
    type: 'shopper' | 'business' | 'driver' | 'admin' | 'system';
    name?: string;
  };
  timestamp: Date;
  metadata?: Record<string, any>;
  error?: string;
}

interface OrderStateHistoryProps {
  orderId: string;
  history: StateHistoryEntry[];
}

const actorIcons = {
  shopper: User,
  business: Store,
  driver: Truck,
  admin: Shield,
  system: Bot,
};

const actorColors = {
  shopper: 'bg-blue-100 text-blue-600',
  business: 'bg-purple-100 text-purple-600',
  driver: 'bg-green-100 text-green-600',
  admin: 'bg-amber-100 text-amber-600',
  system: 'bg-gray-100 text-gray-600',
};

export function OrderStateHistory({ orderId, history }: OrderStateHistoryProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Clock className="h-5 w-5 text-body" />
          <span>State History</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-6 top-0 bottom-0 w-px bg-gray-200" />
          
          <div className="space-y-6">
            {history.map((entry, idx) => {
              const ActorIcon = actorIcons[entry.actor.type];
              const isError = !!entry.error;
              const isLatest = idx === 0;
              
              return (
                <div key={entry.id} className="relative flex space-x-4">
                  {/* Timeline dot */}
                  <div className={cn(
                    "relative z-10 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0",
                    actorColors[entry.actor.type]
                  )}>
                    <ActorIcon className="h-5 w-5" />
                  </div>
                  
                  {/* Content */}
                  <div className={cn(
                    "flex-1 pb-6",
                    isLatest && "bg-purple-50 -mx-2 px-4 py-3 rounded-xl"
                  )}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-heading">
                          {entry.action.replace(/_/g, ' ')}
                        </p>
                        <p className="text-sm text-body">
                          {entry.fromState} → {entry.toState}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-body">
                          {new Date(entry.timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                        </p>
                        <p className="text-xs text-gray-400">
                          {new Date(entry.timestamp).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    
                    {/* Actor Info */}
                    <div className="mt-2 flex items-center space-x-2 text-sm">
                      <span className={cn(
                        "px-2 py-0.5 rounded-full text-xs font-medium capitalize",
                        actorColors[entry.actor.type]
                      )}>
                        {entry.actor.type}
                      </span>
                      {entry.actor.name && (
                        <span className="text-body">{entry.actor.name}</span>
                      )}
                      <span className="text-gray-400">ID: {entry.actor.id.slice(-8)}</span>
                    </div>
                    
                    {/* Metadata */}
                    {entry.metadata && Object.keys(entry.metadata).length > 0 && (
                      <div className="mt-2 bg-gray-50 rounded-lg p-3 text-xs font-mono">
                        {Object.entries(entry.metadata).map(([key, value]) => (
                          <div key={key} className="flex">
                            <span className="text-gray-500 w-24">{key}:</span>
                            <span className="text-heading">{JSON.stringify(value)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Error */}
                    {entry.error && (
                      <div className="mt-2 bg-error/10 text-error rounded-lg p-3 text-sm flex items-start space-x-2">
                        <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                        <span>{entry.error}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


